// Holidays where the Guve's places are, as a hint when planning sessions
// (PRD.md, item 3): some houses open on them, others close, so the CMS only
// marks them. National ones, the state of Rio de Janeiro's and the cities of
// Rio and Niterói's, checked on 14/10/2026:
//   * national: Lei 662/1949, 6.802/1980 and 14.759/2023 (20/11), with Good
//     Friday; Carnival Monday is an optional day off (ponto facultativo);
//   * RJ: São Jorge (Lei 5.198/2008), Carnival Tuesday (Lei 5.243/2008) and
//     Corpus Christi (state law of 2025);
//   * Rio: São Sebastião (20/01); Niterói: its anniversary (22/11).
// A new city or state joins here.

const pad = (value: number) => String(value).padStart(2, '0')
const iso = (year: number, month: number, day: number) => `${year}-${pad(month)}-${pad(day)}`

// Easter Sunday (the anonymous Gregorian algorithm), from which Carnival,
// Good Friday and Corpus Christi follow.
function easter(year: number) {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1
  return Date.UTC(year, month - 1, day)
}

const fromEaster = (year: number, days: number) => {
  const date = new Date(easter(year) + days * 86_400_000)
  return iso(date.getUTCFullYear(), date.getUTCMonth() + 1, date.getUTCDate())
}

const normalize = (value: string | null | undefined) => (value ?? '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase()

const CITIES: Record<string, [number, number, string][]> = {
  'rio de janeiro': [[1, 20, 'São Sebastião']],
  niteroi: [[11, 22, 'Aniversário de Niterói']],
}

// The holidays of one year at a place, by day ("2026-10-12" → its name).
export function holidaysOf(year: number, city?: string | null, state: string | null = 'RJ') {
  const days = new Map<string, string>([
    [iso(year, 1, 1), 'Confraternização Universal'],
    [fromEaster(year, -48), 'Carnaval (ponto facultativo)'],
    [fromEaster(year, -47), 'Carnaval'],
    [fromEaster(year, -2), 'Sexta-feira Santa'],
    [iso(year, 4, 21), 'Tiradentes'],
    [iso(year, 5, 1), 'Dia do Trabalho'],
    [fromEaster(year, 60), 'Corpus Christi'],
    [iso(year, 9, 7), 'Independência'],
    [iso(year, 10, 12), 'Nossa Senhora Aparecida'],
    [iso(year, 11, 2), 'Finados'],
    [iso(year, 11, 15), 'Proclamação da República'],
    [iso(year, 11, 20), 'Consciência Negra'],
    [iso(year, 12, 25), 'Natal'],
  ])
  if ((state ?? 'RJ') === 'RJ') days.set(iso(year, 4, 23), 'São Jorge')
  else {
    // Outside Rio de Janeiro, Carnival Tuesday and Corpus Christi are only
    // optional days off.
    days.set(fromEaster(year, -47), 'Carnaval (ponto facultativo)')
    days.set(fromEaster(year, 60), 'Corpus Christi (ponto facultativo)')
  }
  for (const [month, day, name] of CITIES[normalize(city)] ?? []) days.set(iso(year, month, day), name)
  return days
}

// The holiday on a day, if any.
export function holidayOn(date: string, city?: string | null, state?: string | null) {
  return holidaysOf(Number(date.slice(0, 4)), city, state ?? 'RJ').get(date.slice(0, 10)) ?? null
}
